import { useContext, useEffect, useRef, useState } from "react";
import { Outlet, useLocation, Navigate, useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, Loader2, CheckCircle2 } from "lucide-react";
import {
  filterAttendancePageTabsByRole,
  isAttendanceNavActive,
} from "../../../Moduels/attendance/attendanceNavConfig";
import { useOtHoursAllocationNav } from "../../../hooks/useOtHoursAllocationNav";
import { fetchWfSetupStatus } from "../../../Moduels/attendance/wfApi";
import { AuthContext } from "../../../Moduels/Context/AuthContext";
import { HrPageShell } from "../../hr/HrDesignSystem";
import {
  isAttendanceHub,
  isAttendanceSetupPath,
  pathAllowed,
  OPERATIONAL_ALLOWED_PREFIXES,
  ATTENDANCE_ONBOARDING_PATH,
} from "./attendanceSetupGate";
import "../../../styles/IndianCalendar.css";
import "../../../styles/attendance-dashboard.css";

function isAdminRole(role) {
  const r = String(role || "").toLowerCase();
  return r === "admin" || r === "superadmin" || r === "super_admin";
}

export default function AttendanceLayout() {
  const { pathname, search } = useLocation();
  const { role } = useContext(AuthContext);
  const navigate = useNavigate();
  const [gate, setGate] = useState({
    loading: true,
    setupRequired: false,
    operationalRequired: false,
    nextRoute: ATTENDANCE_ONBOARDING_PATH,
  });
  const [pageTransition, setPageTransition] = useState(null);
  const transitionTimer = useRef(null);

  useEffect(() => {
    return () => {
      if (transitionTimer.current) clearTimeout(transitionTimer.current);
    };
  }, []);

  useEffect(() => {
    setPageTransition(null);
  }, [pathname]);

  const isHub = isAttendanceHub(pathname);
  const onSetup = isAttendanceSetupPath(pathname);
  const onConfiguration = pathname === "/attendance/configuration" || pathname.startsWith("/attendance/configuration/");
  const onCalendarMaster = pathname.startsWith("/attendance/apply-calendar");
  const onDashboard = pathname === "/attendance/dashboard" || isHub;
  const onHoliday = pathname.startsWith("/holiday-calendar") || pathname.startsWith("/attendance/holiday-calendar");
  const isAdmin = isAdminRole(role);
  const { enabled: otHoursAllocationEnabled } = useOtHoursAllocationNav();
  const navItems = filterAttendancePageTabsByRole(role, {
    otHoursAllocationEnabled,
  });

  useEffect(() => {
    if (!isAdmin) {
      setGate({
        loading: false,
        setupRequired: false,
        operationalRequired: false,
        nextRoute: ATTENDANCE_ONBOARDING_PATH,
      });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const st = await fetchWfSetupStatus();
        if (!cancelled) {
          setGate({
            loading: false,
            setupRequired: !!st.setup_required,
            operationalRequired: !!st.operational_required,
            nextRoute: st.next_task_route || ATTENDANCE_ONBOARDING_PATH,
          });
        }
      } catch {
        if (!cancelled) {
          setGate({
            loading: false,
            setupRequired: false,
            operationalRequired: false,
            nextRoute: ATTENDANCE_ONBOARDING_PATH,
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pathname, isAdmin]);

  if (gate.loading) {
    return (
      <HrPageShell className="p-6 text-slate-500 flex items-center justify-center">
        Loading attendance…
      </HrPageShell>
    );
  }

  if (isAdmin && gate.setupRequired && !onSetup && !onConfiguration && !isHub && !onDashboard) {
    return <Navigate to="/attendance/setup" replace />;
  }

  if (isAdmin && gate.operationalRequired && !pathAllowed(pathname, OPERATIONAL_ALLOWED_PREFIXES)) {
    return <Navigate to={gate.nextRoute} replace />;
  }

  const showModuleNav = !onSetup && !onConfiguration && (onDashboard || onHoliday || onCalendarMaster || pathname.startsWith("/attendance/"));

  const currentIdx = navItems.findIndex((item) => isAttendanceNavActive(pathname, search, item));
  const prevItem = currentIdx > 0 ? navItems[currentIdx - 1] : null;
  const nextItem = currentIdx >= 0 && currentIdx < navItems.length - 1 ? navItems[currentIdx + 1] : null;

  function handlePageNav(targetItem) {
    if (pageTransition) return;
    setPageTransition(targetItem.label);
    if (transitionTimer.current) clearTimeout(transitionTimer.current);
    transitionTimer.current = setTimeout(() => {
      navigate(targetItem.path);
      setPageTransition(null);
      transitionTimer.current = null;
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, 3000);
  }

  return (
    <HrPageShell className="!p-0">
      <Outlet />

      {showModuleNav && navItems.length > 1 && (
        <div className="att-page-nav">
          {pageTransition && (
            <div className="att-page-nav-loading">
              <Loader2 size={18} className="att-page-transition-spinner" />
              <span>Loading {pageTransition}…</span>
            </div>
          )}

          <div className="att-page-nav-inner">
            {prevItem ? (
              <button type="button" className="att-page-nav-btn att-page-nav-btn--prev" onClick={() => handlePageNav(prevItem)} disabled={!!pageTransition}>
                <ChevronLeft size={16} />
                <div className="att-page-nav-btn-text">
                  <span className="att-page-nav-btn-hint">Previous Page</span>
                  <span className="att-page-nav-btn-label">{prevItem.label}</span>
                </div>
              </button>
            ) : (
              <div className="att-page-nav-edge">
                <span>This is the first page</span>
              </div>
            )}

            {nextItem ? (
              <button type="button" className="att-page-nav-btn att-page-nav-btn--next" onClick={() => handlePageNav(nextItem)} disabled={!!pageTransition}>
                <div className="att-page-nav-btn-text" style={{ textAlign: "right" }}>
                  <span className="att-page-nav-btn-hint">Next Page</span>
                  <span className="att-page-nav-btn-label">{nextItem.label}</span>
                </div>
                <ChevronRight size={16} />
              </button>
            ) : (
              <div className="att-page-nav-edge att-page-nav-edge--end">
                <CheckCircle2 size={14} />
                <span>You have reached the last page</span>
              </div>
            )}
          </div>
        </div>
      )}
    </HrPageShell>
  );
}
